import { TechnologyTechnologyModal } from '@components/pages/technology/components/technology-technology-modal.component';
import { Tile } from '@components/shared/tile.component';
import { useTechnologiesQueryOptions } from '@queries/technology.query';
import { useQuery } from '@tanstack/react-query';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserSelectedCompanyId } from '../../../../queries/users.query';
import { TechnologyContainer, type TechnologyGridItem } from './technology-container.component';

type TechnologiesGridTileProps = {
  technologies?: TechnologyGridItem[];
  technologyIds?: string[];
  title?: string;
};

export const TechnologiesGridTile: FunctionComponent<TechnologiesGridTileProps> = (props) => {
  const { technologies: providedTechnologies, technologyIds, title = 'Technologies' } = props;
  const technologiesQueryOptions = useTechnologiesQueryOptions();
  const { data: technologies, isLoading: isTechnologiesPending } = useQuery(technologiesQueryOptions);
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const navigate = useNavigate();
  const sourceTechnologies = providedTechnologies ?? technologies;

  const filteredTechnologies = technologyIds
    ? sourceTechnologies?.filter((technology) => technologyIds.includes(technology.id))
    : sourceTechnologies;

  const hasTechnologies = Boolean(filteredTechnologies && filteredTechnologies.length > 0);
  const allowAddTechnology = Boolean(selectedCompanyId && !technologyIds && !providedTechnologies);
  const isLoading = providedTechnologies ? false : isTechnologiesPending;

  const onNavigateToDetails = (id: string): void => {
    navigate(`/technology/${id}`);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <Tile
        className="min-h-[260px]"
        emptyState={{
          message: allowAddTechnology ? 'Add Technology' : undefined,
          onClick: allowAddTechnology ? openModal : undefined,
          title: 'No Technologies',
        }}
        header={{
          button: allowAddTechnology ? { onClick: openModal, text: '+ Add' } : undefined,
          title,
        }}
        isEmpty={!hasTechnologies}
        isLoading={isLoading}
      >
        <div className="@container w-full">
          <div className="grid w-full grid-cols-1 gap-2 rounded-xl bg-gray-100 p-2 @[34rem]:grid-cols-2">
            {filteredTechnologies?.map((technology) => (
              <TechnologyContainer
                key={technology.id}
                onNavigateToDetails={onNavigateToDetails}
                technology={technology}
              />
            ))}
          </div>
        </div>
      </Tile>
      {allowAddTechnology && (
        <TechnologyTechnologyModal
          isEdit={false}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};
